import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import { ActionButtons } from 'components/Buttons/ActionButtons'
import dialogConfirm from 'components/dialogConfirm'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { OverflownText } from 'components/OverflownText'
import { CompanyGPO } from 'models/companies'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import FbButton from '../../../components/FbUI/FbButton'
import { IconContainer, Input } from '../../../components/Filters/TableSearch'
import { BootstrapInput } from '../../../components/FormUtils/NumericInput'
import * as CommonS from '../../../components/Tables/CommonTable.styles'
import { Modal } from '../../../components/UI/Modal/Modal'
import { handleFormError } from '../../../utils/handleFormError'
import * as S from './styles'

const defaultValuesGPO = {
  name: '',
  description: '',
  url: '',
}

export function GPOTable() {
  const api = useApiService()
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [selectedGPOID, setSelectedGPOID] = useState<number>()
  const [searchQuery, setSearchQuery] = useState('')

  const {
    data: gposData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['gpos'],
    queryFn: async () => {
      return await api.getGPOs({
        params: {
          is_default: false,
        },
      })
    },
  })

  const sortedData = gposData?.sort(
    // Sort by is_default first, then by name
    (a, b) =>
      (b.is_default ? 1 : -1) - (a.is_default ? 1 : -1) ||
      a.name.localeCompare(b.name)
  )

  // Client-side search: all data is loaded at once (no pagination), so filtering in-memory is sufficient.
  const data = useMemo(() => {
    if (!searchQuery || !sortedData) return sortedData
    const q = searchQuery.toLowerCase()
    return sortedData.filter(
      (item) =>
        item.name?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q)
    )
  }, [sortedData, searchQuery])

  const {
    control: control,
    handleSubmit: handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    reset,
  } = useForm({
    defaultValues: defaultValuesGPO,
    mode: 'onSubmit',
  })

  function openModal() {
    setShowModal(true)
  }

  function hideModal() {
    setShowModal(false)
    reset(defaultValuesGPO)
    setSelectedGPOID(undefined)
  }

  const onSubmitCreate = handleSubmit(async (values) => {
    const [err] = await to(
      api.postGPO({
        name: values.name,
        description: values.description,
        url: values.url,
      })
    )
    if (err) handleFormError(err, setError)

    await refetch()
    queryClient.invalidateQueries({ queryKey: ['gpos'] })
    toast.success('GPO created successfully')
    hideModal()
  })

  const onSubmitUpdate = handleSubmit(async (values) => {
    if (!selectedGPOID) return

    const [err] = await to(
      api.updateGPO(selectedGPOID, {
        name: values.name,
        description: values.description,
        url: values.url,
      })
    )
    if (err) handleFormError(err, setError)

    await refetch()
    queryClient.invalidateQueries({ queryKey: ['gpos'] })
    toast.success('GPO updated successfully')
    hideModal()
  })

  async function handleRowDelete(index: number) {
    const id = data?.[index]?.id
    if (!id) return

    if (
      await dialogConfirm(
        'Are you sure you want to delete this GPO?',
        'Delete GPO'
      )
    ) {
      await api.deleteGPO(id)
      void refetch()
      queryClient.invalidateQueries({ queryKey: ['gpos'] })
      toast.success('GPO deleted successfully')
    }
  }

  function handleRowEdit(index: number) {
    setShowModal(true)
    setSelectedGPOID(data?.[index]?.id)
    if (data) reset(data?.[index])
  }

  const columns = useMemo<ColumnDef<CompanyGPO, any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 150,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 200,
        cell: (info) => {
          const value = info.getValue()
          return <OverflownText maxLines={2}>{value}</OverflownText>
        },
      },
      {
        id: 'edit',
        header: 'Actions',
        meta: { rightAlign: true },
        cell: ({ row }) => {
          if (row.original.is_default)
            return (
              <div
                className="flex justify-end"
                style={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  color: '#71717A',
                }}
              >
                Default
              </div>
            )
          return (
            <ActionButtons
              className={'justify-end pr-0'}
              onDelete={() => handleRowDelete(row.index)}
              onEdit={() => handleRowEdit(row.index)}
            />
          )
        },
        size: 70,
      },
    ],
    [data]
  )

  if (isLoading || !sortedData) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          width: '100%',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  const descriptionText = `
  First Bite comes pre-loaded with hundreds of GPOs, but if you find a new one,
  you can create or edit them here.
  `

  return (
    <DataTableContainer>
      <ClientDataTable
        tableHeader={
          <>
            <S.SettingsTableHeader>
              <S.TableDescriptionText>
                <OverflownText
                  tooltipText={descriptionText}
                  maxLines={4}
                  placement="bottom"
                >
                  {descriptionText}
                </OverflownText>
              </S.TableDescriptionText>
            </S.SettingsTableHeader>
            <CommonS.SearchContainer>
              <CommonS.SearchContainerFilters>
                <CommonS.TableSearchContainer>
                  <div style={{ position: 'relative' }}>
                    <IconContainer>
                      <PiMagnifyingGlass />
                    </IconContainer>
                    <Input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search GPOs..."
                      className="form-control"
                      style={{ width: '100%' }}
                    />
                  </div>
                </CommonS.TableSearchContainer>
                <CommonS.TableButtonsContainer>
                  <FbButton onClick={() => openModal()}>Create GPO</FbButton>
                </CommonS.TableButtonsContainer>
              </CommonS.SearchContainerFilters>
            </CommonS.SearchContainer>
          </>
        }
        height={'100%'}
        data={data ?? []}
        columns={columns}
      />
      <Modal
        open={showModal}
        title={selectedGPOID ? 'Edit GPO' : 'Create New GPO'}
        description={
          selectedGPOID ? (
            <>
              Edit your Group Purchasing Organization to reflect your current
              process and performance.
            </>
          ) : (
            <>
              Create a Group Purchasing Organization to reflect your current
              process and its performance.
            </>
          )
        }
        onOpenChange={(open) => !open && hideModal()}
        loading={isSubmitting}
        blockAccept={!isValid}
        onAccept={selectedGPOID ? onSubmitUpdate : onSubmitCreate}
        acceptButtonText={selectedGPOID ? 'Update' : 'Create'}
      >
        <BootstrapInput
          name="name"
          control={control}
          type="text"
          label="Name"
          placeholder="GPO Name"
        />
        <BootstrapInput
          name="url"
          control={control}
          type="text"
          label="URL"
          placeholder="URL"
        />
        <TextareaInput
          name="description"
          control={control}
          type="textarea"
          label="Description"
          placeholder="Description"
        />
      </Modal>
    </DataTableContainer>
  )
}
